import { useEffect, useRef, useState } from "react";
import { X, Hammer, Coins } from "lucide-react";
import { debrisRunReward } from "./stateManager";

const R = "/game/road";
const RUN_MS = 30000;      // 30-second procedural salvage run
const SPAWN_MS = 900;      // rubble spawn cadence
const MAX_PILES = 5;

// A repeatable rubble-clearing loop that bridges the missing Stages 4-9. Level-1
// Jess + Nancy hold the room while the player taps rubble piles for scrap.
export default function DebrisRun({ onComplete, onClose }) {
  const [phase, setPhase] = useState("running"); // running | done
  const [timeLeft, setTimeLeft] = useState(RUN_MS);
  const [piles, setPiles] = useState([]);
  const [cleared, setCleared] = useState(0);
  const [reward, setReward] = useState(0);
  const idRef = useRef(0);

  useEffect(() => {
    if (phase !== "running") return;
    const start = performance.now();
    const tick = setInterval(() => {
      const left = Math.max(0, RUN_MS - (performance.now() - start));
      setTimeLeft(left);
      if (left <= 0) { clearInterval(tick); finish(); }
    }, 100);
    const spawner = setInterval(() => {
      setPiles((cur) => {
        if (cur.length >= MAX_PILES) return cur;
        const id = ++idRef.current;
        return [...cur, {
          id,
          hp: 2 + Math.floor(Math.random() * 2),
          top: 14 + Math.random() * 56,   // % within the arena
          left: 8 + Math.random() * 78,
        }];
      });
    }, SPAWN_MS);
    return () => { clearInterval(tick); clearInterval(spawner); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const finish = () => {
    const scrap = debrisRunReward();
    setReward(scrap);
    setPhase("done");
  };

  const hitPile = (id) => {
    setPiles((cur) => {
      const next = [];
      let popped = false;
      for (const pl of cur) {
        if (pl.id === id) {
          const hp = pl.hp - 1;
          if (hp <= 0) { popped = true; continue; }
          next.push({ ...pl, hp });
        } else next.push(pl);
      }
      if (popped) setCleared((c) => c + 1);
      return next;
    });
  };

  const secs = Math.ceil(timeLeft / 1000);

  return (
    <div className="ss-overlay" data-testid="debris-run" style={{ zIndex: 60, background: "rgba(4,6,12,0.92)" }}>
      <div style={{ width: "min(94vw, 460px)", background: "rgba(10,16,28,0.96)", border: "1px solid rgba(245,197,66,0.5)", borderRadius: 18, padding: 16, boxShadow: "0 0 40px rgba(245,197,66,0.18)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 7, color: "#f5c542", fontWeight: 900, letterSpacing: 0.5 }}>
            <Hammer size={16} /> SALVAGE RUN
          </span>
          <button data-testid="debris-close" onClick={onClose} style={{ background: "none", border: "none", color: "#8fa2bd", cursor: "pointer" }}><X size={18} /></button>
        </div>

        {phase === "running" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#cfe0f5", marginBottom: 8 }}>
              <span data-testid="debris-timer">⏱ {secs}s</span>
              <span data-testid="debris-cleared">Cleared: {cleared}</span>
            </div>
            <div style={{ position: "relative", height: 300, borderRadius: 12, overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)", background: "radial-gradient(120% 90% at 50% 0%, #1a2030 0%, #0a0d16 70%)" }}>
              {piles.map((pl) => (
                <button key={pl.id} data-testid={`debris-pile-${pl.id}`} onClick={() => hitPile(pl.id)}
                  style={{
                    position: "absolute", top: `${pl.top}%`, left: `${pl.left}%`, transform: "translate(-50%,-50%)",
                    width: 44, height: 40, borderRadius: 8, cursor: "pointer",
                    background: "linear-gradient(180deg,#6b6357,#3a3730)", border: "2px solid #8a8172",
                    boxShadow: pl.hp > 1 ? "0 0 10px rgba(0,0,0,0.5)" : "0 0 14px rgba(245,197,66,0.6)",
                    display: "flex", alignItems: "center", justifyContent: "center", color: "#f5c542", fontSize: 16,
                  }}>🪨</button>
              ))}
              {/* Level-1 defenders holding the room (flavour) */}
              <img src={`${R}/hero_jess.png`} alt="" style={{ position: "absolute", bottom: 6, left: "38%", width: 30, height: 38, objectFit: "contain", filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.6))" }} />
              <img src={`${R}/hero_nancy.png`} alt="" style={{ position: "absolute", bottom: 6, left: "54%", width: 30, height: 38, objectFit: "contain", filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.6))" }} />
            </div>
            <p style={{ fontSize: 11, color: "#8fa2bd", textAlign: "center", marginTop: 8 }}>Tap the rubble to clear the corridor. Haul scrap before time runs out.</p>
          </>
        )}

        {phase === "done" && (
          <div style={{ textAlign: "center", padding: "18px 6px" }} data-testid="debris-result">
            <div style={{ color: "#f5c542", fontWeight: 900, fontSize: 18, marginBottom: 6 }}>CORRIDOR CLEARED</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "#ffe08a", fontSize: 26, fontWeight: 900, margin: "10px 0" }}>
              <Coins size={24} /> +{reward} <span style={{ fontSize: 13, color: "#cfe0f5", fontWeight: 700 }}>Scrap</span>
            </div>
            <div style={{ fontSize: 12, color: "#8fa2bd", marginBottom: 16 }}>{cleared} rubble piles broken</div>
            <button className="ss-enter-btn" data-testid="debris-collect" onClick={() => onComplete(reward)}
              style={{ borderColor: "rgba(245,197,66,0.7)", background: "rgba(245,197,66,0.16)", color: "#ffe08a" }}>
              <Coins size={16} /> Collect Scrap
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
